import { ArrowDown, ArrowUp, Minus, TrendingUp } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import type {
  LeaderboardEntry,
  ProgressionDataPoint,
  RadarDataPoint,
} from '@/sections/customerManagement/types';

interface Props {
  entry: LeaderboardEntry;
  progression: ProgressionDataPoint[];
  radarData: RadarDataPoint[];
}

function DeltaIndicator({ delta }: { delta: number | null }) {
  if (delta === null) return null;
  if (delta > 0)
    return (
      <span className="flex items-center gap-0.5 text-xs text-green-500">
        <ArrowUp className="size-3" />
        {delta}
      </span>
    );
  if (delta < 0)
    return (
      <span className="flex items-center gap-0.5 text-xs text-red-500">
        <ArrowDown className="size-3" />
        {Math.abs(delta)}
      </span>
    );
  return <Minus className="text-chariot-text-secondary size-3" />;
}

function get7DayTrend(
  progression: ProgressionDataPoint[]
): 'up' | 'down' | 'flat' {
  if (progression.length < 2) return 'flat';
  const first = progression[0].totalScore;
  const last = progression[progression.length - 1].totalScore;
  if (last > first) return 'up';
  if (last < first) return 'down';
  return 'flat';
}

function getStrongestMetric(radarData: RadarDataPoint[]): string {
  if (radarData.length === 0) return '—';
  const best = radarData.reduce((a, b) => (b.value > a.value ? b : a));
  return best.metric;
}

export function StatCards({ entry, progression, radarData }: Props) {
  const trend = get7DayTrend(progression);
  const scoreDelta =
    progression.length >= 2
      ? progression[progression.length - 1].totalScore -
        progression[0].totalScore
      : null;

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {entry.rank !== null && (
        <Card>
          <CardContent className="p-4">
            <p className="text-chariot-text-secondary text-xs font-medium tracking-wide uppercase">
              Current Rank
            </p>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-chariot-text-primary text-2xl font-bold">
                #{entry.rank}
              </span>
              <DeltaIndicator delta={entry.rankDelta} />
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4">
          <p className="text-chariot-text-secondary text-xs font-medium tracking-wide uppercase">
            Total Score
          </p>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-chariot-text-primary text-2xl font-bold">
              {entry.totalScore.toFixed(1)}
            </span>
            {scoreDelta !== null && scoreDelta !== 0 && (
              <span
                className={`text-xs ${scoreDelta > 0 ? 'text-green-500' : 'text-red-500'}`}
              >
                {scoreDelta > 0 ? '+' : ''}
                {scoreDelta.toFixed(1)}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-chariot-text-secondary text-xs font-medium tracking-wide uppercase">
            Strongest Metric
          </p>
          <p className="text-chariot-text-primary mt-1 text-2xl font-bold">
            {getStrongestMetric(radarData)}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-chariot-text-secondary text-xs font-medium tracking-wide uppercase">
            7-Day Trend
          </p>
          <div className="mt-1 flex items-center gap-1.5">
            <TrendingUp
              className={`size-6 ${
                trend === 'up'
                  ? 'text-green-500'
                  : trend === 'down'
                    ? 'rotate-180 text-red-500'
                    : 'text-chariot-text-secondary'
              }`}
            />
            <span className="text-chariot-text-primary text-lg font-semibold capitalize">
              {trend}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
